// Tijd in Europe/Amsterdam: opmaak voor de interface en prompts, en dagberekeningen voor de planner.

export const TIME_ZONE = 'Europe/Amsterdam';

const dateFmt = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: TIME_ZONE });
const timeFmt = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE });
const shortFmt = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', timeZone: TIME_ZONE });
const isoDayFmt = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: TIME_ZONE });

/** "Vandaag is het donderdag 10 september 2026, het is 15:52 (Europe/Amsterdam)." */
export function dayLine(now: Date): string {
  return `Vandaag is het ${dateFmt.format(now)}, het is ${timeFmt.format(now)} (${TIME_ZONE}).`;
}

/** De dagregel voor de systeemprompt (2.2.59): de dag voluit en alleen het uur, "Vandaag is het donderdag 10 september 2026, het is nu
 * tussen 15:00 en 16:00 (Europe/Amsterdam).". Een vervolgbeurt krijgt de systeemprompt van nu; met de minuut erin (`dayLine`) verschilde
 * die bij elke beurt en schreef de API de hele geschiedenis van het gesprek opnieuw in de promptcache. Met het uur blijft de systeemprompt
 * binnen een uur gelijk, zolang de kennis niet verandert. */
export function hourLine(now: Date): string {
  const hour = localParts(now).hour;
  const at = (h: number) => `${String(h).padStart(2, '0')}:00`;
  return `Vandaag is het ${dateFmt.format(now)}, het is nu tussen ${at(hour)} en ${at((hour + 1) % 24)} (${TIME_ZONE}).`;
}

/** Dag als JJJJ-MM-DD in Amsterdamse tijd. */
export function isoDay(d: Date): string {
  return isoDayFmt.format(d);
}

export function formatTime(d: Date): string {
  return timeFmt.format(d);
}

/** Korte weergave voor lijsten: vandaag alleen de tijd, anders "9 sep" of "9 sep 2025". */
export function formatListDate(d: Date, now = new Date()): string {
  if (isoDay(d) === isoDay(now)) return timeFmt.format(d);
  const sameYear = isoDay(d).slice(0, 4) === isoDay(now).slice(0, 4);
  return sameYear ? shortFmt.format(d) : `${shortFmt.format(d)} ${isoDay(d).slice(0, 4)}`;
}

/** Onderdelen van een tijdstip in Amsterdamse tijd (voor de planner). */
export function localParts(d: Date): { day: string; hour: number; minute: number; weekday: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, hour12: false, weekday: 'short', hour: '2-digit', minute: '2-digit' }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return { day: isoDay(d), hour: Number(get('hour')) % 24, minute: Number(get('minute')), weekday: weekdays.indexOf(get('weekday')) };
}

/** Is het lokale tijdstip "HH:MM" vandaag al gepasseerd (of precies nu)? */
export function isPastLocalTime(now: Date, hhmm: string): boolean {
  const [h, m] = hhmm.split(':').map(Number);
  const p = localParts(now);
  return p.hour > (h ?? 0) || (p.hour === (h ?? 0) && p.minute >= (m ?? 0));
}

export function minutesBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 60_000);
}

const offsetFmt = new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** Hoeveel minuten de Amsterdamse klok op tijdstip `ms` voorloopt op UTC (60 in de winter, 120 in de zomer). */
function offsetMinutes(ms: number): number {
  const parts = offsetFmt.formatToParts(new Date(ms));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60_000);
}

/** Begin en einde (exclusief) van kalenderdag JJJJ-MM-DD in Amsterdamse tijd als UTC-tijdstippen, of null bij een
 *  ongeldige dag. De klok verspringt om 01:00 UTC, nooit rond middernacht; zo'n dag duurt 23 of 25 uur. */
export function localDayRange(day: string): { start: string; end: string } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
  const check = new Date(Date.UTC(y, mo, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo || check.getUTCDate() !== d) return null;
  // Lokale middernacht: middernacht UTC min de afwijking, met de afwijking van dat tijdstip zelf (twee stappen).
  const localMidnight = (utc: number) => utc - offsetMinutes(utc - offsetMinutes(utc) * 60_000) * 60_000;
  return { start: new Date(localMidnight(Date.UTC(y, mo, d))).toISOString(), end: new Date(localMidnight(Date.UTC(y, mo, d + 1))).toISOString() };
}
